<template>
  <div class="el-main">
    <div class="card history-heading" v-show="isHistoryMerged">
      <div class="card-header">
        <h2>学歴・職歴を入力</h2>
        <p>「自動入力」を使うと、入学・卒業・入社の表記を自動で作成できます</p>
        <!--
          「以上」は学歴・職歴を通した表の最終行に1つだけ出るため、
          どちらかのカードではなく学歴・職歴共通の見出し側に置く。
        -->
        <el-checkbox
          v-model="formatData.notDisplayIjyo"
          class="history-heading__option"
          >【以上】を表示しない</el-checkbox
        >
      </div>
    </div>
    <div class="history-sections" :class="{ 'is-merged': isHistoryMerged }">
      <RirekishoEduHistoryCard
        :basic-data="basicData"
        :format-id="formatId"
        :current-card-id="currentCardId"
        :preview-errors="previewErrors"
        :hide-heading="isHistoryMerged"
        compact-title="学歴"
      />
      <RirekishoJobHistoryCard
        :basic-data="basicData"
        :format-id="formatId"
        :current-card-id="currentCardId"
        :preview-errors="previewErrors"
        :hide-heading="isHistoryMerged"
        compact-title="職歴"
      />
    </div>
    <RirekishoLicenseCard
      :basic-data="basicData"
      :format-id="formatId"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :example-sentences="exampleSentences"
    />
    <RirekishoReasonCard
      :format-id="formatId"
      :current-card-id="currentCardId"
      :example-sentences="exampleSentences"
      :textarea-min-rows="textareaMinRows"
      :preview-errors="previewErrors"
    />
    <RirekishoAiReasonCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
    />
    <RirekishoReasonNewCard
      :current-card-id="currentCardId"
      :example-sentences="exampleSentences"
      :textarea-min-rows="textareaMinRows"
      :preview-errors="previewErrors"
    />
    <RirekishoOthersCard
      :current-card-id="currentCardId"
      :format-id="formatId"
      :option-items="optionItems"
    />
    <RirekishoRequestsCard
      :current-card-id="currentCardId"
      :format-id="formatId"
      :basic-data="basicData"
      :preview-errors="previewErrors"
      :example-sentences="exampleSentences"
      @update-basic-data="emit('update-basic-data', $event)"
    />
    <RirekishoEsfDetailCard :current-card-id="currentCardId" />
    <RirekishoRecommendCard :current-card-id="currentCardId" />
    <div class="card" v-show="currentCardId == 'confirm'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>作成した履歴書を確認</h2>
          <p>
            <!-- 作成日をこの画面に取り込んだ書式だけ、作成日の説明を先頭に出す -->
            <template v-if="hasCreateDateSection"
              >実際に書類を持参する日、または郵送する日を選びましょう<br
            /></template>
            修正する場合は、プレビューの修正欄をクリックまたはステップを戻ることで修正することができます
          </p>
        </div>
      </div>
    </div>

    <RirekishoCreateDateCard
      :current-card-id="currentCardId"
      :format-id="formatId"
    />

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <DownloadItem
        :formatId="formatId"
        :isSendYagiPostEnabled="isSendYagiPostEnabled"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        :downloadActiveCollapses="downloadActiveCollapses"
        @update:isSendYagiEmailEnabled="isSendYagiEmailEnabled = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyRirekishoFormatData,
  provideRirekishoFormatData,
  type RirekishoExampleSentences
} from '@/composables/format-edit/useRirekishoFormatData'
import {
  provideRirekishoHistories,
  useRirekishoHistories
} from '@/composables/format-edit/useRirekishoHistories'
import RirekishoEduHistoryCard from '@/components/format-edit/rirekisho/RirekishoEduHistoryCard.vue'
import RirekishoJobHistoryCard from '@/components/format-edit/rirekisho/RirekishoJobHistoryCard.vue'
import RirekishoLicenseCard from '@/components/format-edit/rirekisho/RirekishoLicenseCard.vue'
import RirekishoReasonCard from '@/components/format-edit/rirekisho/RirekishoReasonCard.vue'
import RirekishoAiReasonCard from '@/components/format-edit/rirekisho/RirekishoAiReasonCard.vue'
import RirekishoReasonNewCard from '@/components/format-edit/rirekisho/RirekishoReasonNewCard.vue'
import RirekishoOthersCard from '@/components/format-edit/rirekisho/RirekishoOthersCard.vue'
import RirekishoRequestsCard from '@/components/format-edit/rirekisho/RirekishoRequestsCard.vue'
import RirekishoEsfDetailCard from '@/components/format-edit/rirekisho/RirekishoEsfDetailCard.vue'
import RirekishoRecommendCard from '@/components/format-edit/rirekisho/RirekishoRecommendCard.vue'
import RirekishoCreateDateCard from '@/components/format-edit/rirekisho/RirekishoCreateDateCard.vue'
import DownloadItem from '@/components/formats/CommonParts/DownloadItem.vue'
import type { BasicData } from '@/utils/format-edit/basicData'
import { isCardSectionActive } from '@/utils/format-edit/cardSections'

const props = defineProps<{
  basicData: BasicData
  formatId?: string
  previewFormat?: {
    errorMessages?: Record<string, string>
    optionItems?: Record<string, unknown>
  } | null
  currentCardId?: string
  exampleSentences?: RirekishoExampleSentences
}>()

const emit = defineEmits<{
  (e: 'update-basic-data', payload: Partial<BasicData>): void
}>()

const { formLib } = useHostServices()

const { formatData } = useFormatLocalData({
  saveName: 'rirekisho',
  saveOtherName: 'shokumukeirekisho',
  createEmpty: createEmptyRirekishoFormatData
})
provideRirekishoFormatData(formatData)

const histories = useRirekishoHistories(formatData)
provideRirekishoHistories(histories)

const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiPostEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)
const downloadActiveCollapses = ref(['postcard'])

const previewErrors = computed(() => {
  if (!props.previewFormat || !props.previewFormat.errorMessages) return {}
  return props.previewFormat.errorMessages
})

const optionItems = computed(() => {
  if (!props.previewFormat || !props.previewFormat.optionItems) return {}
  return props.previewFormat.optionItems
})

const getMode = computed(() => process.env.VUE_APP_MODE)

// 学歴・職歴が 1 ステップへ統合された書式かどうか（カード定義の `sections`）
const isHistoryMerged = computed(
  () =>
    isCardSectionActive(props.formatId, props.currentCardId, 'edu-history') &&
    isCardSectionActive(props.formatId, props.currentCardId, 'job-history')
)

// 作成日が確認画面に取り込まれた書式かどうか（独立ステップの書式では false）
const hasCreateDateSection = computed(() =>
  isCardSectionActive(props.formatId, 'confirm', 'create-date')
)

const textareaMinRows = computed(() => {
  if (props.formatId === 'rirekisho_job_new') return 10
  if (props.formatId === 'rirekisho_job_new_single') return 6
  return 3
})

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

watch(
  () => formatData.createDate,
  (newValue) => {
    if (newValue == null) return
    formatData.writeDate = formLib.dateFormat(
      newValue as Date,
      'yyyy年 m月 d日'
    )
  }
)

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
})

defineExpose({
  saveName: 'rirekisho',
  formatData,
  addEducationalHistory: histories.addEducationalHistory
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables';
@import '@/assets/scss/format-edit/document-card';

.history-heading__option {
  margin-top: 0.5rem;
}

.history-sections.is-merged {
  // 基本情報と同じカード表現。カードを載せる下地。
  background: $white;
  padding: 0 1rem 1rem;

  // PC は 学歴 | 職歴 の 2 カラム。モバイルは縦積みのまま。
  @include min-width(md) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 1rem;
    align-items: start;
  }

  /*
   * 履歴の入力アイテム自体が枠を持つため、本文までカードに入れると
   * 枠の中に枠ができて入力域が狭くなる。枠は見出しだけに付け、
   * 【以上】を表示しない以降はカードの外に出す。
   */
  > .card {
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: visible;
  }

  :deep(.card-header) {
    @include document-card-frame;
    @include document-card-header;
  }

  // 学歴・職歴カードの本文は .card-content ではなく .card-content-list。
  // 枠の外に出すので左右の余白は持たせない（上は見出しの margin-bottom で足りる）
  :deep(.card-content-list) {
    padding: 0;
  }

  :deep(.add-item) {
    padding-left: 0;
    padding-right: 0;
  }

  // 「自動入力」は見出しと同じ帯に並ぶ。el-button 既定の 12px 20px では
  // 帯が高くなり、学歴・職歴の見出し高さもズレる。
  :deep(.compact-heading .el-button) {
    padding: 8px;
  }
}

* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}
</style>
